import React from "react";
import PropTypes from "prop-types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UIButton from "../../../ui/UIButton";
import { useSelector } from "react-redux";
import messages from "../../../../assets/locale/messages";

import "./_FormFooter.scss";

const FormFooter = ({
  isFirstStep,
  isLastStep,
  stepName,
  backLabel,
  onBack,
  onSaveDraft,
  onNext,
  saveDraftLoading,
  nextLoading,
  nextDisabled,
  saveDraftDisabled,
  /** Overrides last-step primary label (e.g. company portal edit without publish). */
  lastStepPrimaryLabel,
  /** Overrides the "Save As Draft" label (e.g. "Save" when editing a published event). */
  saveDraftLabel,
  /** Hides the Save As Draft button (e.g. on the last step, where only Finish remains). */
  showSaveDraft,
  /** When set, a low-emphasis Cancel button precedes Save in the right cluster. */
  onCancel,
  cancelLabel,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang];
  const t = events.creatEvent;

  return (
    <footer className="ui-page-form-footer event-form-footer">
      {/* Back button — left side (only rendered when not on the first step) */}
      {!isFirstStep && (
        <div className="event-form-footer__secondary">
          <UIButton
            type="button"
            variant="text"
            color="inherit"
            className="event-form-footer__back"
            startIcon={<ArrowBackIcon className="arrow-back-icon" />}
            onClick={onBack}
            size="medium"
          >
            {backLabel}
          </UIButton>
        </div>
      )}

      {/* Cancel + Save As Draft + Next Step — right side */}
      <div className="event-form-footer__primary-group">
        {onCancel && (
          <UIButton
            type="button"
            variant="text"
            color="inherit"
            className="event-form-footer__cancel"
            onClick={onCancel}
            size="medium"
          >
            {cancelLabel}
          </UIButton>
        )}

        {showSaveDraft && (
          <UIButton
            type="button"
            variant="outline"
            className="event-form-footer__draft"
            loading={saveDraftLoading}
            disabled={saveDraftDisabled}
            onClick={onSaveDraft}
            size="medium"
          >
            {saveDraftLabel ?? t.saveAsDraft}
          </UIButton>
        )}

        <UIButton
          type={isLastStep ? "submit" : "button"}
          appearance="gradient"
          className="event-form-footer__next"
          loading={nextLoading}
          disabled={nextDisabled}
          onClick={onNext}
          size="medium"
        >
          {isLastStep
            ? lastStepPrimaryLabel ?? t.finishAndPublishEvent
            : t.nextStep.replace("{step}", stepName)}
        </UIButton>
      </div>
    </footer>
  );
};

FormFooter.propTypes = {
  isFirstStep: PropTypes.bool,
  isLastStep: PropTypes.bool,
  backLabel: PropTypes.string,
  onBack: PropTypes.func,
  onSaveDraft: PropTypes.func,
  onNext: PropTypes.func,
  saveDraftLoading: PropTypes.bool,
  nextLoading: PropTypes.bool,
  nextDisabled: PropTypes.bool,
  saveDraftDisabled: PropTypes.bool,
  /** Label for the next step; unused on the last step (finish button). */
  stepName: PropTypes.string,
  lastStepPrimaryLabel: PropTypes.string,
  saveDraftLabel: PropTypes.string,
  showSaveDraft: PropTypes.bool,
  onCancel: PropTypes.func,
  cancelLabel: PropTypes.string,
};

FormFooter.defaultProps = {
  isFirstStep: false,
  isLastStep: false,
  backLabel: "Back",
  onBack: undefined,
  onSaveDraft: undefined,
  onNext: undefined,
  saveDraftLoading: false,
  nextLoading: false,
  nextDisabled: false,
  saveDraftDisabled: false,
  stepName: "",
  lastStepPrimaryLabel: undefined,
  saveDraftLabel: undefined,
  showSaveDraft: true,
  onCancel: undefined,
  cancelLabel: "Cancel",
};

export default FormFooter;
